import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CogIcon,
  ChevronDownIcon,
  LockClosedIcon,
} from "@heroicons/react/outline";
import Pagination from "components/lib/Shared/Pagination";
import SearchInput from "components/lib/Shared/SearchInput";
import useComplianceAccess from "hooks/useComplianceAccess";

const formatPromptMonth = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getMonth() + 1;
  }
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= 12) {
    return numeric;
  }
  return null;
};

const sortMonths = (months) => [...months].sort((a, b) => (a || 0) - (b || 0));

const arraysEqual = (first = [], second = []) => {
  if (first.length !== second.length) return false;
  for (let i = 0; i < first.length; i++) {
    if (first[i] !== second[i]) return false;
  }
  return true;
};

const EMPTY_ARRAY = [];

const ComplianceTrackerTable = ({
  entities,
  formatDate,
  maxTaskSelections = 12,
}) => {
  const { hasRegistrarRole } = useComplianceAccess();

  const matrix = useMemo(() => {
    const activeEntities = entities.map((entity) => ({
      ...entity,
      tasks: (entity.tasks || []).filter(
        (task) =>
          task && (task.lifecycleStatus === "active" || !task.lifecycleStatus)
      ),
    }));

    const taskTitles = [];
    const mandatoryTitleSet = new Set();

    activeEntities.forEach((entity) => {
      entity.tasks.forEach((task) => {
        if (task?.title && !taskTitles.includes(task.title)) {
          taskTitles.push(task.title);
        }
        if (task?.isMandatory) {
          mandatoryTitleSet.add(task.title);
        }
      });
    });

    const rows = activeEntities.map((entity) => {
      const deadlineMap = new Map();
      entity.tasks.forEach((task) => {
        if (!task?.title) return;
        const key = task.title;
        if (!deadlineMap.has(key)) {
          const dateValue = task.deadline || task.nextCheckDate;
          deadlineMap.set(key, {
            display: dateValue ? formatDate(dateValue) : "—",
            raw: dateValue || null,
            complianceTag: task.complianceTag,
            complianceColor: task.complianceColor,
          });
        }
      });
      return {
        id: entity.id,
        name: entity.name,
        label: entity.label,
        deadlines: deadlineMap,
        promptMonth: entity.promptMonth
          ? formatPromptMonth(entity.promptMonth)
          : formatPromptMonth(entity.annualReturnDate),
      };
    });

    return { taskTitles, rows, mandatoryTitles: Array.from(mandatoryTitleSet) };
  }, [entities, formatDate]);

  const [selectedTaskTitles, setSelectedTaskTitles] = useState(
    () => EMPTY_ARRAY
  );
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const configRef = useRef(null);

  const mandatoryTitleSet = useMemo(
    () => new Set(matrix.mandatoryTitles),
    [matrix.mandatoryTitles]
  );

  // Always include mandatories + fill with optionals
  const buildSelection = useCallback(
    (preferredOptionals = []) => {
      const mandatories = matrix.taskTitles.filter((t) =>
        mandatoryTitleSet.has(t)
      );
      const seen = new Set(mandatories);

      const orderedOptionals = [];
      // First add preferred (previously selected) optionals
      preferredOptionals.forEach((title) => {
        if (!seen.has(title) && matrix.taskTitles.includes(title)) {
          orderedOptionals.push(title);
          seen.add(title);
        }
      });
      // Then fill the rest
      matrix.taskTitles.forEach((title) => {
        if (!seen.has(title)) {
          orderedOptionals.push(title);
          seen.add(title);
        }
      });

      const final = [
        ...mandatories,
        ...orderedOptionals.slice(0, maxTaskSelections - mandatories.length),
      ];

      return final;
    },
    [matrix.taskTitles, mandatoryTitleSet, maxTaskSelections]
  );

  // Initialize selected tasks: mandatories + some optionals
  useEffect(() => {
    setSelectedTaskTitles((current) => {
      const currentOptionals = current.filter(
        (t) => !mandatoryTitleSet.has(t) && matrix.taskTitles.includes(t)
      );
      const next = buildSelection(currentOptionals);
      return arraysEqual(next, current) ? current : next;
    });
  }, [buildSelection, mandatoryTitleSet, matrix.taskTitles]);

  const visibleTaskTitles = useMemo(() => {
    return selectedTaskTitles.filter((title) =>
      matrix.taskTitles.includes(title)
    );
  }, [selectedTaskTitles, matrix.taskTitles]);

  const optionalSelectedCount = useMemo(
    () => selectedTaskTitles.filter((t) => !mandatoryTitleSet.has(t)).length,
    [selectedTaskTitles, mandatoryTitleSet]
  );

  const optionalCapacity = Math.max(
    maxTaskSelections - mandatoryTitleSet.size,
    0
  );

  // Click outside handler
  useEffect(() => {
    if (!isConfigOpen) return;

    const handleClickOutside = (event) => {
      if (
        configRef.current &&
        !configRef.current.contains(event.target) &&
        !event.target.closest?.("[data-task-config-trigger]")
      ) {
        setIsConfigOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isConfigOpen]);

  // Month filter logic (unchanged)
  const uniqueMonths = useMemo(() => {
    const monthSet = new Set();
    matrix.rows.forEach((row) => {
      if (row.promptMonth) monthSet.add(row.promptMonth);
    });
    return sortMonths(monthSet);
  }, [matrix.rows]);

  const [selectedMonths, setSelectedMonths] = useState(() => EMPTY_ARRAY);
  const [isMonthFilterOpen, setIsMonthFilterOpen] = useState(false);
  const monthFilterRef = useRef(null);

  useEffect(() => {
    setSelectedMonths((current) => {
      if (!uniqueMonths.length) return current.length ? EMPTY_ARRAY : current;
      const intersection = current.filter((m) => uniqueMonths.includes(m));
      return intersection.length ? intersection : uniqueMonths;
    });
  }, [uniqueMonths]);

  // Month filter click outside
  useEffect(() => {
    if (!isMonthFilterOpen) return;
    const handleClickOutside = (event) => {
      if (
        monthFilterRef.current &&
        !monthFilterRef.current.contains(event.target) &&
        !event.target.closest?.("[data-month-filter-trigger]")
      ) {
        setIsMonthFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMonthFilterOpen]);

  // Search & pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const monthFilteredRows = useMemo(() => {
    if (!hasRegistrarRole || !selectedMonths.length) return matrix.rows;
    if (selectedMonths.length === uniqueMonths.length) return matrix.rows;
    return matrix.rows.filter((row) =>
      selectedMonths.includes(row.promptMonth)
    );
  }, [hasRegistrarRole, matrix.rows, selectedMonths, uniqueMonths]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return monthFilteredRows;
    const query = searchTerm.toLowerCase();
    return monthFilteredRows.filter((row) => {
      const baseMatch =
        row.name.toLowerCase().includes(query) ||
        (row.label || "").toLowerCase().includes(query) ||
        String(row.promptMonth ?? "")
          .toLowerCase()
          .includes(query);

      if (baseMatch) return true;

      return visibleTaskTitles.some((title) => {
        const dl = row.deadlines.get(title);
        return (
          (dl?.display || "").toLowerCase().includes(query) ||
          (dl?.complianceTag || "").toLowerCase().includes(query)
        );
      });
    });
  }, [monthFilteredRows, searchTerm, visibleTaskTitles]);

  const totalRows = filteredRows.length;
  const rowsPerPage = totalRows <= 15 ? Math.max(totalRows, 1) : 10;
  const lastPage = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  useEffect(() => {
    setCurrentPage((p) => (p > lastPage ? lastPage : p));
  }, [lastPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const paginatedRows = useMemo(() => {
    if (!totalRows) return [];
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [currentPage, filteredRows, rowsPerPage, totalRows]);

  const startEntry = totalRows ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const endEntry = totalRows
    ? Math.min(startEntry + rowsPerPage - 1, totalRows)
    : 0;

  const toggleTaskSelection = useCallback(
    (title) => {
      if (mandatoryTitleSet.has(title)) return; // cannot uncheck mandatory

      setSelectedTaskTitles((current) => {
        const optionals = current.filter((t) => !mandatoryTitleSet.has(t));
        const isSelected = optionals.includes(title);

        let nextOptionals;
        if (isSelected) {
          nextOptionals = optionals.filter((t) => t !== title);
        } else {
          if (optionals.length >= optionalCapacity) return current;
          nextOptionals = [...optionals, title];
        }

        return buildSelection(nextOptionals);
      });
    },
    [mandatoryTitleSet, buildSelection, optionalCapacity]
  );

  if (!matrix.rows.length) {
    return (
      <div className="rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-500">
        No active tasks available for the selected entities.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {matrix.taskTitles.length > maxTaskSelections && (
            <div className="relative">
              <button
                type="button"
                data-task-config-trigger
                onClick={() => {
                  setIsConfigOpen((prev) => !prev);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <CogIcon className="h-4 w-4" aria-hidden="true" />
                Configure tasks
              </button>

              {isConfigOpen && (
                <div
                  ref={configRef}
                  className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
                >
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <p className="text-sm font-semibold text-gray-800">
                      Select visible tasks
                    </p>
                    <span className="text-xs text-gray-500">
                      {visibleTaskTitles.length} / {maxTaskSelections}
                    </span>
                  </div>

                  <div className="mt-3 max-h-64 overflow-y-auto pr-1">
                    {matrix.taskTitles.map((title) => {
                      const isChecked = selectedTaskTitles.includes(title);
                      const isMandatory = mandatoryTitleSet.has(title);
                      const canToggle =
                        !isMandatory &&
                        (isChecked || optionalSelectedCount < optionalCapacity);

                      return (
                        <label
                          key={title}
                          className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-sm ${
                            canToggle
                              ? "cursor-pointer hover:bg-gray-50"
                              : "cursor-not-allowed opacity-60"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={!canToggle}
                            onChange={() => toggleTaskSelection(title)}
                            className={`mt-0.5 h-4 w-4 rounded border-gray-300 text-sflPrimary focus:ring-sflPrimary ${
                              !canToggle ? "cursor-not-allowed" : ""
                            }`}
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-800">
                              {title}
                            </span>
                            {isMandatory && (
                              <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-sflPrimary/40 bg-sflPrimary/10 px-2 py-0.5 text-xs font-semibold text-sflPrimary">
                                <LockClosedIcon className="h-3 w-3" />
                                Mandatory
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex justify-between border-t border-gray-100 pt-3 text-sm">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedTaskTitles(buildSelection(matrix.taskTitles))
                      }
                      className="font-medium text-sflPrimary hover:underline"
                    >
                      Show maximum
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedTaskTitles(buildSelection([]))}
                      className="text-gray-600 hover:underline"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {hasRegistrarRole && uniqueMonths.length > 0 && (
            <div className="relative">
              <button
                type="button"
                data-month-filter-trigger
                onClick={() => setIsMonthFilterOpen((p) => !p)}
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <span>Prompt Month</span>
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              </button>
              {/* Month filter dropdown - kept as is */}
            </div>
          )}
        </div>

        <SearchInput
          value={searchTerm}
          handleOnChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search corporates, tasks or deadlines"
          className="w-full sm:w-72 md:w-80"
        />
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white max-h-[60vh] sm:max-h-[65vh] lg:max-h-[70vh]">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Corporate / Company</th>
              <th className="px-4 py-3">Prompt Month</th>
              {visibleTaskTitles.map((title) => (
                <th key={title} className="px-4 py-3 text-center">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {totalRows ? (
              paginatedRows.map((row) => (
                <tr key={row.id} className="transition-colors hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-700">
                    <div className="flex flex-col gap-0.5">
                      <span>{row.name}</span>
                      {row.label && (
                        <span className="text-xs text-gray-500">
                          {row.label}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {row.promptMonth || "—"}
                  </td>
                  {visibleTaskTitles.map((title) => {
                    const dl = row.deadlines.get(title);
                    return (
                      <td key={title} className="px-4 py-3 text-center">
                        {dl ? (
                          <span
                            className={`inline-flex min-w-[6.5rem] items-center justify-center rounded-full px-3 py-1 text-xs font-semibold ${
                              dl.complianceColor || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {dl.display}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={visibleTaskTitles.length + 2}
                  className="px-4 py-6 text-center text-sm text-gray-500"
                >
                  No results match the current search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalRows > 0 && (
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          setCurrentPage={setCurrentPage}
        >
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startEntry}</span> to{" "}
            <span className="font-medium">{endEntry}</span> of{" "}
            <span className="font-medium">{totalRows}</span> entries
          </p>
        </Pagination>
      )}
    </div>
  );
};

ComplianceTrackerTable.propTypes = {
  entities: PropTypes.array.isRequired,
  formatDate: PropTypes.func.isRequired,
  maxTaskSelections: PropTypes.number,
};

export default ComplianceTrackerTable;
